import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, Paper, Button, Chip, Collapse,
  useMediaQuery, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";

// 中2(J2) 単語ビューア。週(=章, 1stNN)を選ぶと単語カードを表示。
// Gemini音声(米/英/例文/米2読み)は audio_targets から再生ボタンを出す（採用済みのみ）。
// 既存の jane/gcpF 音声(アメリカ人女性/２)は比較用に残す。
// QR遷移用に ?week_num=1stNN&homework=1 を受け取り、その週をプリセレクト＋宿題(★)のみ表示。
function WordViewerJ2Page({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const homeworkOnly = searchParams.get("homework") === "1";
  const weekParam = searchParams.get("week_num");

  const [words, setWords] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [openExamples, setOpenExamples] = useState({});
  const [playingKey, setPlayingKey] = useState(null);
  const audioRefs = useRef({});

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { state: { from: '/word_viewer_j2' }, replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetch(`${API_BASE}/j2/word_weeks`)
      .then((r) => r.json())
      .then((data) => {
        const weeks = Array.isArray(data) ? data.map((w) => w.week_num) : [];
        setWeekOptions(weeks);
        if (weeks.length > 0) setSelectedWeek(weekParam && weeks.includes(weekParam) ? weekParam : weeks[0]);
      })
      .catch((e) => console.error("week fetch error:", e));
  }, [API_BASE, weekParam]);

  useEffect(() => {
    if (selectedWeek) {
      fetch(`${API_BASE}/j2/words_by_week/${selectedWeek}`)
        .then((r) => r.json())
        .then((data) => setWords(Array.isArray(data) ? data : []))
        .catch((e) => console.error("API fetch error:", e));
      setOpenExamples({});
    }
  }, [selectedWeek, API_BASE]);

  const toggleExample = (i) => setOpenExamples((p) => ({ ...p, [i]: !p[i] }));

  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach((a) => {
      if (a && !a.paused) { a.pause(); a.currentTime = 0; }
    });
    setPlayingKey(null);
  };

  const handlePlay = (key) => {
    stopAllAudio();
    const a = audioRefs.current[key];
    if (a) { a.play(); setPlayingKey(key); }
  };

  // 隠しaudio＋再生/停止ボタンを1つ描画（汎用）
  const renderButton = (key, label, src, color) => (
    <React.Fragment key={key}>
      {playingKey === key ? (
        <Button variant="outlined" color="error" sx={{ minWidth: 110 }} onClick={stopAllAudio}>停止</Button>
      ) : (
        <Button variant="contained" color={color} sx={{ minWidth: 110 }} onClick={() => handlePlay(key)}>{label}</Button>
      )}
      <audio style={{ display: "none" }} ref={(el) => (audioRefs.current[key] = el)}
             onEnded={() => setPlayingKey(null)}>
        <source src={src} type="audio/mpeg" />
      </audio>
    </React.Fragment>
  );

  // ラベル: 通常=「再生」/ 米2読み=「発音１・発音２」/ 米英差=「アメリカ発音・イギリス発音」
  const geminiLabel = (t, hasUk, hasDual) => {
    if (t.kind === "word") {
      if (hasUk) return t.accent === "uk" ? "イギリス発音" : "アメリカ発音";
      if (hasDual) return t.variant === 2 ? "発音２" : "発音１";
      return "再生";
    }
    if (hasUk) return t.accent === "uk" ? "例文 イギリス発音" : "例文 アメリカ発音";
    return "例文 再生";
  };

  if (!isAuthenticated) return null;

  const shown = homeworkOnly ? words.filter((w) => w.homework === "★") : words;

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: isSmallScreen ? 10 : 6 }}>
        <Box sx={{ marginY: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="week-label">週番号を選択</InputLabel>
            <Select labelId="week-label" value={selectedWeek} label="週番号を選択"
                    onChange={(e) => setSelectedWeek(e.target.value)}>
              {weekOptions.map((w, i) => <MenuItem key={i} value={w}>{w}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {homeworkOnly && (
          <Box sx={{ marginBottom: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="宿題のみ表示" color="warning" />
            <Typography variant="body2" color="text.secondary">この週の宿題（★）単語だけを表示しています</Typography>
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={3}>
          {shown.map((item, index) => {
            const hasExample = Boolean(item.example || item.example_jp);
            const targets = item.audio_targets || [];
            const wordTargets = targets.filter((t) => t.kind === "word" && t.exists);
            const exampleTargets = targets.filter((t) => t.kind === "example" && t.exists);
            // ラベル判定は計画全体（未生成含む）で見る
            const hasUk = targets.some((t) => t.kind === "word" && t.accent === "uk");
            const hasDual = targets.some((t) => t.kind === "word" && t.variant === 2);
            const v = item.voices || {};
            const ev = item.example_voices || {};
            return (
              <Paper key={item.id || index} sx={{ padding: 2 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                  {item.homework === "★" && <Chip label="宿題" color="warning" size="small" />}
                  <Typography variant="h4" gutterBottom>{item.word}</Typography>
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {item.pos && <Typography component="span" color="primary" sx={{ marginRight: 0.5 }}>({item.pos})</Typography>}
                  {item.meaning}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, marginTop: 1, flexWrap: "wrap" }}>
                  {/* Gemini（米/英/米2読み） */}
                  {wordTargets.map((t) =>
                    renderButton(`${index}_${t.kind}_${t.accent}_${t.variant}`, geminiLabel(t, hasUk, hasDual),
                      `${AUDIO_BASE}/audio_clipped/${t.logical}?voice=${t.voice}`,
                      t.accent === "uk" ? "secondary" : "primary"))}
                  {/* 既存(jane/gcpF) 比較用 */}
                  {v.us_female && renderButton(`${index}_legacy_f`, "アメリカ人女性",
                    `${AUDIO_BASE}/audio_clipped/${item.word_audio_logical}?voice=us_female`, "inherit")}
                  {v.us_female2 && renderButton(`${index}_legacy_f2`, "アメリカ人女性２",
                    `${AUDIO_BASE}/audio_clipped/${item.word_audio_logical}?voice=us_female2`, "inherit")}
                  {hasExample && (
                    <Button variant="outlined" onClick={() => toggleExample(index)}>
                      例文{openExamples[index] ? " ▲" : " ▼"}
                    </Button>
                  )}
                </Box>

                {hasExample && (
                  <Collapse in={Boolean(openExamples[index])}>
                    <Box sx={{ marginTop: 1.5, paddingLeft: 1, borderLeft: "3px solid #90caf9" }}>
                      {item.example && <Typography variant="body1">{item.example}</Typography>}
                      {item.example_jp && <Typography variant="body2" color="text.secondary">{item.example_jp}</Typography>}
                      {(exampleTargets.length > 0 || ev.us_female || ev.us_female2) && (
                        <Box sx={{ display: "flex", gap: 1, marginTop: 1, flexWrap: "wrap" }}>
                          {exampleTargets.map((t) =>
                            renderButton(`${index}_${t.kind}_${t.accent}_${t.variant}`, geminiLabel(t, hasUk, hasDual),
                              `${AUDIO_BASE}/audio_clipped/${t.logical}?voice=${t.voice}`,
                              t.accent === "uk" ? "secondary" : "primary"))}
                          {ev.us_female && renderButton(`${index}_ex_legacy_f`, "例文 女性",
                            `${AUDIO_BASE}/audio_clipped/${item.example_audio_logical}?voice=us_female`, "inherit")}
                          {ev.us_female2 && renderButton(`${index}_ex_legacy_f2`, "例文 女性２",
                            `${AUDIO_BASE}/audio_clipped/${item.example_audio_logical}?voice=us_female2`, "inherit")}
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                )}
              </Paper>
            );
          })}
        </Box>
      </Container>
    </>
  );
}

export default WordViewerJ2Page;
